import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext'; // Adjust path as needed
import { StoryService } from '../../services/storyService'; // Adjust path as needed
import { useLocation } from 'react-router-dom';
import ShareStoryModal from './GraphicNovelEditor/components/Canvas/ShareStoryModal'; //

interface TextElement {
  id: string;
  content: string;
  x: number;
  y: number;
  fontSize: number;
  fontFamily: string;
  color: string;
  isDragging: boolean;
  rotation: number;
  isBold: boolean;
  isItalic: boolean;
}

interface DrawingElement {
  id: string;
  type: 'path' | 'shape';
  points: { x: number; y: number }[];
  color: string;
  width: number;
  opacity: number;
  tool: 'pen' | 'brush' | 'marker' | 'pencil' | 'eraser' | 'rectangle' | 'circle' | 'star' | 'heart';
  pattern?: 'solid' | 'dashed' | 'dotted';
  fill?: boolean;
  fillColor?: string;
}

interface BookPage {
  id: number;
  backgroundColor: string;
  textElements: TextElement[];
  drawingElements: DrawingElement[];
  backgroundImage?: string;
  backgroundPattern?: string;
}

const PictureBookEditor: React.FC = () => {
  const [book, setBook] = useState<BookPage[]>([
    { 
      id: 1, 
      backgroundColor: '#ffffff',
      textElements: [],
      drawingElements: [],
      backgroundPattern: 'none'
    }
  ]);
  
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [bookTitle, setBookTitle] = useState('My Picture Book');
  const [authorName, setAuthorName] = useState('');
  const [savedStatus, setSavedStatus] = useState('');
  const [isFlipping, setIsFlipping] = useState(false);
  const [showingTips, setShowingTips] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [activeToolTab, setActiveToolTab] = useState('draw');
  const [currentMode, setCurrentMode] = useState<'draw' | 'text' | 'select'>('draw');
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentColor, setCurrentColor] = useState('#FF6B35');
  const [currentTool, setCurrentTool] = useState<'pen' | 'brush' | 'marker' | 'pencil' | 'eraser' | 'rectangle' | 'circle' | 'star' | 'heart'>('brush');
  const [brushSize, setBrushSize] = useState(5);
  const [opacity, setOpacity] = useState(100);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [selectedDrawingId, setSelectedDrawingId] = useState<string | null>(null);
  const [isDraggingElement, setIsDraggingElement] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [undoStack, setUndoStack] = useState<BookPage[][]>([]);
  const [redoStack, setRedoStack] = useState<BookPage[][]>([]);
  const [customColor, setCustomColor] = useState('#000000');
  const [fillShapes, setFillShapes] = useState(false);
  const [pattern, setPattern] = useState<'solid' | 'dashed' | 'dotted'>('solid');
  const [currentStoryStarter, setCurrentStoryStarter] = useState('A magical creature who lives in your backyard');
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const { currentUser } = useAuth();
  const [storyId, setStoryId] = useState<string | null>(null);
  const location = useLocation();
  
  const canvasRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const theme = {
    primary: '#FF6B35',
    secondary: '#9B59B6',
    accent: '#F39C12',
    success: '#2ECC71',
    pink: '#E91E63',
    blue: '#3498DB'
  };

   // Load story useEffect
   useEffect(() => {
    const loadStory = async () => {
      const state = location.state;
      if (state?.storyId && currentUser) {
        try {
          const story = await StoryService.getStory(currentUser.uid, state.storyId);
          if (story && story.content) {
            setStoryId(story.id || null);
            if (story.content.pages) {
              setBook(story.content.pages);
            }
            if (story.title) {
              setBookTitle(story.title);
            }
          }
        } catch (error) {
          console.error('Error loading story:', error);
        }
      }
    };

    loadStory();
  }, [location.state, currentUser]);

  const colorPalettes = [
    {
      name: 'Creative',
      colors: [theme.primary, theme.secondary, theme.accent, theme.success, theme.pink, theme.blue, '#000000', '#ffffff', '#E74C3C', '#95A5A6']
    },
    {
      name: 'Pastels',
      colors: ['#FFE5E5', '#E5F3FF', '#F0E5FF', '#E5FFE5', '#FFF5E5', '#FFE5F5', '#F5F5F5', '#E5E5E5', '#FFECEC', '#ECFFEC']
    },
    {
      name: 'Nature',
      colors: ['#2E8B57', '#228B22', '#32CD32', '#7CFC00', '#ADFF2F', '#9ACD32', '#6B8E23', '#556B2F', '#8FBC8F', '#90EE90']
    },
    {
      name: 'Ocean',
      colors: ['#001F3F', '#0074D9', '#7FDBFF', '#39CCCC', '#01FF70', '#2ECC40', '#3D9970', '#111111', '#85144B', '#B10DC9']
    }
  ];

  const [activeColorPalette, setActiveColorPalette] = useState(0);

  const backgroundPatterns = [
    { name: 'None', value: 'none' },
    { name: 'Dots', value: 'radial-gradient(circle, #e5e7eb 1px, transparent 1px)' },
    { name: 'Grid', value: 'linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)' },
    { name: 'Lines', value: 'repeating-linear-gradient(45deg, transparent, transparent 10px, #e5e7eb 10px, #e5e7eb 11px)' }
  ];

  const fontFamilies = [
    'Comic Sans MS', 'Arial', 'Verdana', 'Georgia', 'Times New Roman', 
    'Trebuchet MS', 'Impact', 'Courier New', 'Brush Script MT', 'Papyrus'
  ];

  const fontSizes = [12, 16, 20, 24, 28, 32, 40, 48, 56, 64];

  const storyStarters = [
    "A magical creature who lives in your backyard",
    "A book that comes to life when you read it",
    "A child who can talk to plants",
    "A mysterious door that appears in different places",
    "A pet that has a secret superpower",
    "A cloud that grants wishes",
    "A treehouse that travels through time",
    "A paintbrush that makes drawings real"
  ];

  const drawingTools = [
    { name: 'Brush', icon: '🖌️', tool: 'brush' as const, color: theme.primary },
    { name: 'Pen', icon: '✒️', tool: 'pen' as const, color: theme.secondary },
    { name: 'Marker', icon: '🖊️', tool: 'marker' as const, color: theme.blue },
    { name: 'Pencil', icon: '✏️', tool: 'pencil' as const, color: theme.accent },
    { name: 'Eraser', icon: '🧹', tool: 'eraser' as const, color: theme.pink }
  ];

  const shapeTools = [
    { name: 'Rectangle', icon: '▭', tool: 'rectangle' as const, color: theme.primary },
    { name: 'Circle', icon: '●', tool: 'circle' as const, color: theme.success },
    { name: 'Star', icon: '⭐', tool: 'star' as const, color: theme.accent },
    { name: 'Heart', icon: '💖', tool: 'heart' as const, color: theme.pink }
  ];

  const saveState = () => {
    setUndoStack(prev => [...prev.slice(-19), [...book]]);
    setRedoStack([]);
  };

  const undo = () => {
    if (undoStack.length > 0) {
      const previousState = undoStack[undoStack.length - 1];
      setRedoStack(prev => [book, ...prev.slice(0, 19)]);
      setBook(previousState);
      setUndoStack(prev => prev.slice(0, -1));
    }
  };

  const redo = () => {
    if (redoStack.length > 0) {
      const nextState = redoStack[0];
      setUndoStack(prev => [...prev.slice(-19), book]);
      setBook(nextState);
      setRedoStack(prev => prev.slice(1));
    }
  };

  const getNewStoryStarter = () => {
    const randomStarter = storyStarters[Math.floor(Math.random() * storyStarters.length)];
    setCurrentStoryStarter(randomStarter);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (book[currentPageIndex]) {
        handleAutoSave();
      }
    }, 30000);
    
    return () => clearTimeout(timer);
  }, [book, currentPageIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'z':
            e.preventDefault();
            if (e.shiftKey) {
              redo();
            } else {
              undo();
            }
            break;
          case 's':
            e.preventDefault();
            handleSave();
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undoStack, redoStack]);
  
  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBookTitle(event.target.value);
    setSavedStatus('');
  };
  
  const handleAuthorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAuthorName(event.target.value);
    setSavedStatus('');
  };
  
  const handleSave = async () => {
    if (!currentUser) {
      setSavedStatus('Please sign in to save your story');
      setTimeout(() => setSavedStatus(''), 3000);
      return;
    }
  
    try {
      const storyData = {
        title: bookTitle || 'Untitled Picture Book',
        type: 'picture-book' as const,
        content: {
          pages: book, // Your picture book data
          // Include any other relevant data
        },
        description: `A picture book with ${book.length} pages`,
        pageCount: book.length,
        author: currentUser.displayName || 'Anonymous'
      };
  
      if (storyId) {
        await StoryService.updateStory(currentUser.uid, storyId, storyData);
      } else {
        const newStoryId = await StoryService.createStory(currentUser.uid, storyData);
        setStoryId(newStoryId);
      }
      
      setSavedStatus('Picture book saved to library! 💾');
    } catch (error) {
      console.error('Error saving story:', error);
      setSavedStatus('Failed to save story');
    }
    
    setTimeout(() => setSavedStatus(''), 3000);
  };
  
  const handleAutoSave = async () => {
    if (!currentUser || !storyId) return;
    
    try {
      const content = {
        pages: book,
        // Your current picture book data
      };
      
      await StoryService.autoSaveStory(currentUser.uid, storyId, content);
      setSavedStatus('Auto-saved ✓');
      setTimeout(() => setSavedStatus(''), 2000);
    } catch (error) {
      // Don't show error for auto-save failures to avoid disrupting workflow
      console.error('Auto-save failed:', error);
    }
  };
  
  const addNewPage = () => {
    saveState();
    const newPage: BookPage = {
      id: book.length + 1,
      backgroundColor: '#ffffff',
      textElements: [],
      drawingElements: [],
      backgroundPattern: 'none'
    };
    setBook([...book, newPage]);
    setCurrentPageIndex(book.length);
  };
  
  const navigateToPage = (index: number) => {
    if (index >= 0 && index < book.length && index !== currentPageIndex) {
      setIsFlipping(true);
      setTimeout(() => {
        setCurrentPageIndex(index);
        setIsFlipping(false);
      }, 300);
    }
  };

  const nextPage = () => {
    if (currentPageIndex < book.length - 1) {
      navigateToPage(currentPageIndex + 1);
    }
  };

  const prevPage = () => {
    if (currentPageIndex > 0) {
      navigateToPage(currentPageIndex - 1);
    }
  };

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    if (currentMode === 'select') {
      // Check if we clicked on a text element
      const currentPage = book[currentPageIndex];
      const clickedTextElement = currentPage.textElements.find(element => {
        return (
          x >= element.x && 
          x <= element.x + 200 && 
          y >= element.y && 
          y <= element.y + 40
        );
      });
      
      if (clickedTextElement) {
        setSelectedElementId(clickedTextElement.id);
        setSelectedDrawingId(null);
        setIsDraggingElement(true);
        setDragOffset({
          x: x - clickedTextElement.x,
          y: y - clickedTextElement.y
        });
        return;
      }
      
      // Check if we clicked on a drawing element
      const clickedDrawingElement = currentPage.drawingElements.find(drawing => {
        if (drawing.points.length === 0) return false;
        
        // For shapes, check if click is within bounds
        if (shapeTools.some(t => t.tool === drawing.tool) && drawing.points.length >= 2) {
          const start = drawing.points[0];
          const end = drawing.points[1];
          const left = Math.min(start.x, end.x);
          const right = Math.max(start.x, end.x);
          const top = Math.min(start.y, end.y);
          const bottom = Math.max(start.y, end.y);
          
          return x >= left && x <= right && y >= top && y <= bottom;
        }
        
        // For paths, check if click is near any point
        return drawing.points.some(point => {
          const distance = Math.sqrt(Math.pow(x - point.x, 2) + Math.pow(y - point.y, 2));
          return distance <= drawing.width + 10; // Add some tolerance
        });
      });
      
      if (clickedDrawingElement) {
        setSelectedDrawingId(clickedDrawingElement.id);
        setSelectedElementId(null);
        setIsDraggingElement(true);
        
        // Calculate offset for drawing elements
        if (clickedDrawingElement.points.length > 0) {
          const firstPoint = clickedDrawingElement.points[0];
          setDragOffset({
            x: x - firstPoint.x,
            y: y - firstPoint.y
          });
        }
        return;
      }
      
      // If we didn't click on anything, deselect
      setSelectedElementId(null);
      setSelectedDrawingId(null);
      return;
    }
    
    if (currentMode === 'draw') {
      saveState();
      setIsDrawing(true);
      
      const updatedBook = [...book];
      const currentPage = {...updatedBook[currentPageIndex]};
      
      const newDrawingElement: DrawingElement = {
        id: `drawing-${Date.now()}`,
        type: shapeTools.some(t => t.tool === currentTool) ? 'shape' : 'path',
        points: [{ x, y }],
        color: currentTool === 'eraser' ? 'transparent' : currentColor,
        width: brushSize,
        opacity: opacity / 100,
        tool: currentTool,
        pattern,
        fill: fillShapes && shapeTools.some(t => t.tool === currentTool),
        fillColor: fillShapes ? currentColor : undefined
      };
      
      currentPage.drawingElements = [...currentPage.drawingElements, newDrawingElement];
      updatedBook[currentPageIndex] = currentPage;
      setBook(updatedBook);
    } else if (currentMode === 'text') {
      saveState();
      const newTextId = `text-${Date.now()}`;
      
      const updatedBook = [...book];
      const currentPage = {...updatedBook[currentPageIndex]};
      
      currentPage.textElements = [
        ...currentPage.textElements,
        {
          id: newTextId,
          content: 'Type your text here...',
          x,
          y,
          fontSize: fontSizes[3],
          fontFamily: fontFamilies[0],
          color: currentColor,
          isDragging: false,
          rotation: 0,
          isBold: false,
          isItalic: false
        }
      ];
      
      updatedBook[currentPageIndex] = currentPage;
      setBook(updatedBook);
      setSelectedElementId(newTextId);
      setCurrentMode('select');
    }
  };
  
  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    if (currentMode === 'select' && isDraggingElement) {
      const updatedBook = [...book];
      const currentPage = {...updatedBook[currentPageIndex]};
      
      // Handle text element dragging
      if (selectedElementId) {
        currentPage.textElements = currentPage.textElements.map(element => 
          element.id === selectedElementId 
            ? {...element, x: x - dragOffset.x, y: y - dragOffset.y}
            : element
        );
      }
      
      // Handle drawing element dragging
      if (selectedDrawingId) {
        const selectedDrawing = currentPage.drawingElements.find(d => d.id === selectedDrawingId);
        if (selectedDrawing && selectedDrawing.points.length > 0) {
          const deltaX = (x - dragOffset.x) - selectedDrawing.points[0].x;
          const deltaY = (y - dragOffset.y) - selectedDrawing.points[0].y;
          
          currentPage.drawingElements = currentPage.drawingElements.map(drawing => 
            drawing.id === selectedDrawingId 
              ? {
                  ...drawing,
                  points: drawing.points.map(point => ({
                    x: point.x + deltaX,
                    y: point.y + deltaY
                  }))
                }
              : drawing
          );
        }
      }
      
      updatedBook[currentPageIndex] = currentPage;
      setBook(updatedBook);
      return;
    }
    
    if (currentMode === 'draw' && isDrawing) {
      const updatedBook = [...book];
      const currentPage = {...updatedBook[currentPageIndex]};
      
      if (currentPage.drawingElements.length > 0) {
        const lastDrawingIndex = currentPage.drawingElements.length - 1;
        const lastElement = currentPage.drawingElements[lastDrawingIndex];
        
        if (shapeTools.some(t => t.tool === lastElement.tool)) {
          lastElement.points = [lastElement.points[0], { x, y }];
        } else {
          lastElement.points.push({ x, y });
        }
        
        updatedBook[currentPageIndex] = currentPage;
        setBook(updatedBook);
      }
    }
  };
  
  const handleCanvasMouseUp = () => {
    setIsDrawing(false);
    setIsDraggingElement(false);
  };

  const handleTextEdit = (id: string, content: string) => {
    const updatedBook = [...book];
    const currentPage = {...updatedBook[currentPageIndex]};
    
    currentPage.textElements = currentPage.textElements.map(element => 
      element.id === id ? {...element, content} : element
    );
    
    updatedBook[currentPageIndex] = currentPage;
    setBook(updatedBook);
  };

  const toggleTextStyle = (id: string, style: 'bold' | 'italic') => {
    const updatedBook = [...book];
    const currentPage = {...updatedBook[currentPageIndex]};
    
    currentPage.textElements = currentPage.textElements.map(element => 
      element.id === id 
        ? {...element, [style === 'bold' ? 'isBold' : 'isItalic']: !element[style === 'bold' ? 'isBold' : 'isItalic']}
        : element
    );
    
    updatedBook[currentPageIndex] = currentPage;
    setBook(updatedBook);
  };

  const rotateText = (id: string, degrees: number) => {
    const updatedBook = [...book];
    const currentPage = {...updatedBook[currentPageIndex]};
    
    currentPage.textElements = currentPage.textElements.map(element => 
      element.id === id 
        ? {...element, rotation: (element.rotation + degrees) % 360}
        : element
    );
    
    updatedBook[currentPageIndex] = currentPage;
    setBook(updatedBook);
  };

  const duplicateElement = () => {
    if (!selectedElementId) return;
    
    saveState();
    const updatedBook = [...book];
    const currentPage = {...updatedBook[currentPageIndex]};
    
    const selectedText = currentPage.textElements.find(el => el.id === selectedElementId);
    if (selectedText) {
      const newElement = {
        ...selectedText,
        id: `text-${Date.now()}`,
        x: selectedText.x + 20,
        y: selectedText.y + 20
      };
      currentPage.textElements.push(newElement);
    }
    
    updatedBook[currentPageIndex] = currentPage;
    setBook(updatedBook);
  };

  const changeBackgroundColor = (color: string) => {
    saveState();
    const updatedBook = [...book];
    updatedBook[currentPageIndex] = {
      ...updatedBook[currentPageIndex],
      backgroundColor: color
    };
    setBook(updatedBook);
  };

  const changeBackgroundPattern = (pattern: string) => {
    saveState();
    const updatedBook = [...book];
    updatedBook[currentPageIndex] = {
      ...updatedBook[currentPageIndex],
      backgroundPattern: pattern
    };
    setBook(updatedBook);
  };

  const renderDrawings = (drawingElements: DrawingElement[]) => {
    return drawingElements.map((drawing) => {
      if (drawing.points.length < 1) return null;
      
      const isSelected = selectedDrawingId === drawing.id;
      
      const style: React.CSSProperties = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: currentMode === 'select' ? 'auto' : 'none',
        opacity: drawing.opacity,
        cursor: currentMode === 'select' ? 'move' : 'default'
      };

      if (drawing.tool === 'eraser') {
        style.mixBlendMode = 'multiply';
      }

      if (shapeTools.some(t => t.tool === drawing.tool) && drawing.points.length >= 2) {
        const start = drawing.points[0];
        const end = drawing.points[1];
        const width = Math.abs(end.x - start.x);
        const height = Math.abs(end.y - start.y);
        const left = Math.min(start.x, end.x);
        const top = Math.min(start.y, end.y);

        return (
          <svg key={drawing.id} style={style}>
            {isSelected && (
              <rect
                x={left - 5}
                y={top - 5}
                width={width + 10}
                height={height + 10}
                stroke={theme.blue}
                strokeWidth="2"
                strokeDasharray="5,5"
                fill="none"
                pointerEvents="none"
              />
            )}
            {drawing.tool === 'rectangle' && (
              <rect
                x={left}
                y={top}
                width={width}
                height={height}
                stroke={drawing.color}
                strokeWidth={drawing.width}
                fill={drawing.fill ? drawing.fillColor : 'none'}
                strokeDasharray={drawing.pattern === 'dashed' ? '5,5' : drawing.pattern === 'dotted' ? '2,2' : ''}
              />
            )}
            {drawing.tool === 'circle' && (
              <ellipse
                cx={left + width / 2}
                cy={top + height / 2}
                rx={width / 2}
                ry={height / 2}
                stroke={drawing.color}
                strokeWidth={drawing.width}
                fill={drawing.fill ? drawing.fillColor : 'none'}
                strokeDasharray={drawing.pattern === 'dashed' ? '5,5' : drawing.pattern === 'dotted' ? '2,2' : ''}
              />
            )}
            {drawing.tool === 'star' && (
              <polygon
                points={`${left + width/2},${top} ${left + width*0.6},${top + height*0.35} ${left + width},${top + height*0.35} ${left + width*0.7},${top + height*0.6} ${left + width*0.8},${top + height} ${left + width/2},${top + height*0.75} ${left + width*0.2},${top + height} ${left + width*0.3},${top + height*0.6} ${left},${top + height*0.35} ${left + width*0.4},${top + height*0.35}`}
                stroke={drawing.color}
                strokeWidth={drawing.width}
                fill={drawing.fill ? drawing.fillColor : 'none'}
                strokeDasharray={drawing.pattern === 'dashed' ? '5,5' : drawing.pattern === 'dotted' ? '2,2' : ''}
              />
            )}
          </svg>
        );
      } else if (drawing.points.length >= 2) {
        let pathData = `M ${drawing.points[0].x} ${drawing.points[0].y}`;
        for (let i = 1; i < drawing.points.length; i++) {
          pathData += ` L ${drawing.points[i].x} ${drawing.points[i].y}`;
        }
        
        // Calculate bounding box for selection indicator
        const minX = Math.min(...drawing.points.map(p => p.x));
        const maxX = Math.max(...drawing.points.map(p => p.x));
        const minY = Math.min(...drawing.points.map(p => p.y));
        const maxY = Math.max(...drawing.points.map(p => p.y));
        
        return (
          <svg key={drawing.id} style={style}>
            {isSelected && (
              <rect
                x={minX - 10}
                y={minY - 10}
                width={maxX - minX + 20}
                height={maxY - minY + 20}
                stroke={theme.blue}
                strokeWidth="2"
                strokeDasharray="5,5"
                fill="none"
                pointerEvents="none"
              />
            )}
            <path
              d={pathData}
              stroke={drawing.color}
              strokeWidth={drawing.width}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={drawing.pattern === 'dashed' ? '5,5' : drawing.pattern === 'dotted' ? '2,2' : ''}
              opacity={drawing.tool === 'marker' ? 0.7 : 1}
            />
          </svg>
        );
      }
      
      return null;
    });
  };

  const renderTextElements = (textElements: TextElement[]) => {
    return textElements.map((element) => {
      const isSelected = selectedElementId === element.id;
      
      return (
        <div 
          key={element.id}
          style={{
            position: 'absolute',
            left: `${element.x}px`,
            top: `${element.y}px`,
            fontSize: `${element.fontSize}px`,
            fontFamily: element.fontFamily,
            color: element.color,
            fontWeight: element.isBold ? 'bold' : 'normal',
            fontStyle: element.isItalic ? 'italic' : 'normal',
            transform: `rotate(${element.rotation}deg)`,
            transformOrigin: 'top left',
            cursor: currentMode === 'select' ? 'move' : 'default',
            border: isSelected ? `2px dashed ${theme.blue}` : 'none',
            padding: '8px',
            minWidth: '120px',
            background: isSelected ? `${theme.blue}20` : 'transparent',
            borderRadius: '4px'
          }}
          onClick={() => setSelectedElementId(element.id)}
        >
          {isSelected ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <textarea
                value={element.content}
                onChange={(e) => handleTextEdit(element.id, e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  resize: 'none',
                  background: 'rgba(255,255,255,0.9)',
                  fontSize: `${element.fontSize}px`,
                  fontFamily: element.fontFamily,
                  color: element.color,
                  fontWeight: element.isBold ? 'bold' : 'normal',
                  fontStyle: element.isItalic ? 'italic' : 'normal'
                }}
                rows={3}
                autoFocus
              />
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                padding: '12px',
                background: 'rgba(255,255,255,0.95)',
                borderRadius: '12px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                border: `2px solid ${theme.blue}30`
              }}>
                <select 
                  value={element.fontFamily}
                  onChange={(e) => {
                    const updatedBook = [...book];
                    const currentPage = {...updatedBook[currentPageIndex]};
                    currentPage.textElements = currentPage.textElements.map(el => 
                      el.id === element.id ? {...el, fontFamily: e.target.value} : el
                    );
                    updatedBook[currentPageIndex] = currentPage;
                    setBook(updatedBook);
                  }}
                  style={{
                    padding: '6px 12px',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '12px',
                    background: 'white'
                  }}
                >
                  {fontFamilies.map(font => (
                    <option key={font} value={font}>{font}</option>
                  ))}
                </select>
                
                <select 
                  value={element.fontSize}
                  onChange={(e) => {
                    const updatedBook = [...book];
                    const currentPage = {...updatedBook[currentPageIndex]};
                    currentPage.textElements = currentPage.textElements.map(el => 
                      el.id === element.id ? {...el, fontSize: parseInt(e.target.value)} : el
                    );
                    updatedBook[currentPageIndex] = currentPage;
                    setBook(updatedBook);
                  }}
                  style={{
                    padding: '6px 12px',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '12px',
                    background: 'white'
                  }}
                >
                  {fontSizes.map(size => (
                    <option key={size} value={size}>{size}px</option>
                  ))}
                </select>
                
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={() => toggleTextStyle(element.id, 'bold')}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      border: '2px solid',
                      background: element.isBold ? theme.primary : 'white',
                      borderColor: element.isBold ? theme.primary : '#ddd',
                      color: element.isBold ? 'white' : '#333',
                      cursor: 'pointer'
                    }}
                  >
                    B
                  </button>
                  <button
                    onClick={() => toggleTextStyle(element.id, 'italic')}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontStyle: 'italic',
                      border: '2px solid',
                      background: element.isItalic ? theme.secondary : 'white',
                      borderColor: element.isItalic ? theme.secondary : '#ddd',
                      color: element.isItalic ? 'white' : '#333',
                      cursor: 'pointer'
                    }}
                  >
                    I
                  </button>
                </div>
                
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={() => rotateText(element.id, -15)}
                    style={{
                      padding: '6px 8px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      border: '2px solid #ddd',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                    title="Rotate Left"
                  >
                    ↺
                  </button>
                  <button
                    onClick={() => rotateText(element.id, 15)}
                    style={{
                      padding: '6px 8px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      border: '2px solid #ddd',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                    title="Rotate Right"
                  >
                    ↻
                  </button>
                </div>
                
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={duplicateElement}
                    style={{
                      padding: '6px 8px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      border: '2px solid #ddd',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                    title="Duplicate"
                  >
                    📋
                  </button>
                  <button 
                    onClick={() => {
                      const updatedBook = [...book];
                      const currentPage = {...updatedBook[currentPageIndex]};
                      currentPage.textElements = currentPage.textElements.filter(el => el.id !== element.id);
                      updatedBook[currentPageIndex] = currentPage;
                      setBook(updatedBook);
                      setSelectedElementId(null);
                    }}
                    style={{
                      padding: '6px 8px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      border: '2px solid #ffcdd2',
                      background: '#ffebee',
                      color: '#d32f2f',
                      cursor: 'pointer'
                    }}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ whiteSpace: 'pre-wrap' }}>{element.content}</div>
          )}
        </div>
      );
    });
  };

  const getPageStyle = (page: BookPage) => {
    const style: React.CSSProperties = {
      backgroundColor: page.backgroundColor,
      backgroundSize: page.backgroundPattern === 'none' ? 'cover' : '20px 20px',
      backgroundPosition: 'center',
      backgroundRepeat: page.backgroundPattern === 'none' ? 'no-repeat' : 'repeat'
    };

    if (page.backgroundImage) {
      style.backgroundImage = `url(${page.backgroundImage})`;
    } else if (page.backgroundPattern && page.backgroundPattern !== 'none') {
      style.backgroundImage = page.backgroundPattern;
    }

    return style;
  };

  const shareStory = () => {
    const storyText = `Check out my picture book "${bookTitle}" by ${authorName}! It has ${book.length} pages!`;
    if (navigator.share) {
      navigator.share({ title: bookTitle, text: storyText, url: window.location.href });
    } else {
      navigator.clipboard?.writeText(storyText);
      alert('Story details copied to clipboard!');
    }
  };


  return (
    <div style={{
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: `linear-gradient(135deg, ${theme.secondary} 0%, ${theme.primary} 50%, ${theme.accent} 100%)`,
      minHeight: '100vh'
    }}>
      {/* Standardized Header - Only change made here */}
      <div style={{
        background: 'white',
        borderRadius: '0 0 25px 25px',
        padding: '20px 25px',
        marginBottom: '20px',
        boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
        border: `4px solid ${theme.primary}`,
        borderTop: 'none',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', top: '10%', left: '8%', width: '30px', height: '30px',
          background: theme.pink, borderRadius: '50%', opacity: 0.15
        }} />
        <div style={{
          position: 'absolute', bottom: '15%', right: '10%', width: '25px', height: '25px',
          background: theme.success, borderRadius: '50%', opacity: 0.2
        }} />
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '15px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
              borderRadius: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
              fontSize: '28px'
            }}>
              📚
            </div>
            
            <div>
              <h1 style={{
                fontSize: '2.2rem',
                fontWeight: '900',
                margin: 0,
                background: `linear-gradient(45deg, ${theme.primary}, ${theme.secondary})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {bookTitle}
              </h1>
              <p style={{
                fontSize: '1rem',
                color: '#666',
                fontWeight: '600',
                margin: 0
              }}>
                {authorName ? `by ${authorName}` : 'Create amazing picture books!'}
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              style={{
                background: `linear-gradient(135deg, ${theme.blue}, ${theme.secondary})`,
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '700',
                boxShadow: `0 8px 25px ${theme.blue}40`
              }}
            >
              📖 {isPreviewMode ? 'Edit Mode' : 'Preview'}
            </button>
            
            <button
              onClick={handleSave}
              style={{
                background: `linear-gradient(135deg, ${theme.success}, ${theme.accent})`,
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '700',
                boxShadow: `0 8px 25px ${theme.success}40`
              }}
            >
              💾 Save to Library
            </button>
            
            <button
              onClick={() => setShowShareModal(true)}
              style={{
                background: `linear-gradient(135deg, ${theme.blue}, ${theme.pink})`,
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '700',
                boxShadow: `0 8px 25px ${theme.blue}40`
              }}
            >
              🌟 Share Story
            </button>
          </div>
        </div>
      </div>

      

         {/* ShareStoryModal - replacing the old Share Modal */}
         {showShareModal && (
        <ShareStoryModal
          projectName={bookTitle}
          author={authorName || currentUser?.displayName || 'Anonymous'}
          pages={book.map(page => ({ ...page, id: page.id.toString() }))}
          onClose={() => setShowShareModal(false)}
          onShare={(data) => {
            console.log('Share story:', data);
            shareStory();
            setShowShareModal(false);
          }}
          onPublishToGallery={(data) => {
            console.log('Publish to gallery:', data);
            setShowShareModal(false);
          }}
        />
      )}

      {isPreviewMode ? (
        /* Preview Mode */
        <div style={{ minHeight: '100vh', background: '#1a1a1a', color: 'white' }}>
          <div style={{ padding: '30px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px' }}>
              <button 
                onClick={() => setIsPreviewMode(false)} 
                style={{
                  padding: '12px 20px',
                  background: '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
              >
              </button>
              <div style={{ textAlign: 'center' }}>
                <h1 style={{ fontSize: '3rem', fontWeight: '900', margin: '0 0 8px 0' }}>{bookTitle}</h1>
                <p style={{ color: '#ccc', fontSize: '1.2rem', margin: 0 }}>by {authorName}</p>
              </div>
              <div style={{ width: '140px' }}></div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '40px' }}>
              <button 
                onClick={prevPage} 
                disabled={currentPageIndex === 0}
                style={{
                  padding: '20px',
                  background: '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: currentPageIndex === 0 ? 'not-allowed' : 'pointer',
                  opacity: currentPageIndex === 0 ? 0.5 : 1,
                  fontSize: '20px',
                  width: '60px',
                  height: '60px'
                }}
              >
                ←
              </button>
              
              <div 
                style={{
                  width: '500px',
                  height: '500px',
                  background: 'white',
                  borderRadius: '20px',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                  transform: isFlipping ? 'scale(0.95) rotateY(10deg)' : 'scale(1)',
                  transition: 'transform 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  ...getPageStyle(book[currentPageIndex])
                }}
              >
                {renderDrawings(book[currentPageIndex]?.drawingElements || [])}
                {renderTextElements(book[currentPageIndex]?.textElements || [])}
                <div style={{
                  position: 'absolute', bottom: '16px', right: '16px',
                  background: 'rgba(0,0,0,0.7)', color: 'white',
                  padding: '8px 12px', borderRadius: '8px',
                  fontSize: '14px', fontWeight: '600'
                }}>
                  {currentPageIndex + 1} / {book.length}
                </div>
              </div>
              
              <button 
                onClick={nextPage} 
                disabled={currentPageIndex === book.length - 1}
                style={{
                  padding: '20px',
                  background: '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: currentPageIndex === book.length - 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPageIndex === book.length - 1 ? 0.5 : 1,
                  fontSize: '20px',
                  width: '60px',
                  height: '60px'
                }}
              >
                →
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Editor Mode */
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 20px 20px' }}>
          <div style={{ display: 'flex', gap: '20px', height: 'calc(100vh - 160px)' }}>
            {/* Left Panel - Tools */}
            <div style={{
              width: leftPanelCollapsed ? '60px' : '320px',
              transition: 'width 0.3s ease',
              background: 'white',
              borderRadius: '20px',
              boxShadow: '0 12px 35px rgba(0,0,0,0.1)',
              border: `3px solid ${theme.primary}`,
              position: 'relative',
              overflow: 'hidden'
            }}>
              <button
                onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '15px',
                  background: theme.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  zIndex: 10
                }}
              >
                {leftPanelCollapsed ? '→' : '←'}
              </button>

              {!leftPanelCollapsed && (
                <div style={{ padding: '25px', height: '100%', overflowY: 'auto' }}>
                  {/* Tool Tabs */}
                  <div style={{ marginBottom: '25px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {[
                        { id: 'draw', label: 'Drawing Tools', icon: '🎨' },
                        { id: 'text', label: 'Text & Fonts', icon: '📝' },
                        { id: 'background', label: 'Backgrounds', icon: '🌈' },
                        { id: 'tips', label: 'Writing Tips', icon: '💡' }
                      ].map(tab => (
                        <button
                          key={tab.id}
                          onClick={() => {
                            setActiveToolTab(tab.id);
                            if (tab.id === 'draw') setCurrentMode('draw');
                            if (tab.id === 'text') setCurrentMode('text');
                            if (tab.id === 'tips') setShowingTips(!showingTips);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px 16px',
                            borderRadius: '12px',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '700',
                            background: activeToolTab === tab.id 
                              ? `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` 
                              : 'transparent',
                            color: activeToolTab === tab.id ? 'white' : '#666',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <span style={{ fontSize: '18px' }}>{tab.icon}</span>
                          <span>{tab.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tool Content Area */}
                  <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    paddingRight: '10px'
                  }}>
                    {/* Drawing Tools */}
                    {activeToolTab === 'draw' && (
                      <div>
                        <h3 style={{
                          margin: '0 0 20px 0',
                          fontSize: '1.2rem',
                          fontWeight: '800',
                          background: `linear-gradient(45deg, ${theme.primary}, ${theme.secondary})`,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}>
                          🖌️ Drawing Tools
                        </h3>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '25px' }}>
                          {drawingTools.map(tool => (
                            <button
                              key={tool.tool}
                              onClick={() => setCurrentTool(tool.tool)}
                              style={{
                                padding: '16px',
                                borderRadius: '12px',
                                border: `3px solid ${currentTool === tool.tool ? tool.color : '#ddd'}`,
                                background: currentTool === tool.tool 
                                  ? `${tool.color}10` 
                                  : 'white',
                                cursor: 'pointer',
                                textAlign: 'center',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              <div style={{ fontSize: '24px', marginBottom: '8px' }}>{tool.icon}</div>
                              <div style={{ fontSize: '12px', fontWeight: '700', color: '#333' }}>{tool.name}</div>
                            </button>
                          ))}
                        </div>

                        <h4 style={{
                          margin: '0 0 15px 0',
                          fontSize: '1rem',
                          fontWeight: '700',
                          color: theme.success
                        }}>
                          ⭐ Shapes
                        </h4>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '25px' }}>
                          {shapeTools.map(tool => (
                            <button
                              key={tool.tool}
                              onClick={() => setCurrentTool(tool.tool)}
                              style={{
                                padding: '16px',
                                borderRadius: '12px',
                                border: `3px solid ${currentTool === tool.tool ? tool.color : '#ddd'}`,
                                background: currentTool === tool.tool 
                                  ? `${tool.color}10` 
                                  : 'white',
                                cursor: 'pointer',
                                textAlign: 'center',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              <div style={{ fontSize: '24px', marginBottom: '8px' }}>{tool.icon}</div>
                              <div style={{ fontSize: '12px', fontWeight: '700', color: '#333' }}>{tool.name}</div>
                            </button>
                          ))}
                        </div>

                        {/* Brush Settings */}
                        <div style={{ marginBottom: '25px' }}>
                          <h4 style={{
                            margin: '0 0 15px 0',
                            fontSize: '1rem',
                            fontWeight: '700',
                            color: theme.accent
                          }}>
                            ⚙️ Settings
                          </h4>
                          
                          <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '8px' }}>
                              Opacity: {opacity}%
                            </label>
                            <input 
                              type="range" 
                              min="10" 
                              max="100" 
                              value={opacity} 
                              onChange={(e) => setOpacity(parseInt(e.target.value))}
                              style={{
                                width: '100%',
                                height: '6px',
                                borderRadius: '3px',
                                background: `linear-gradient(to right, ${theme.blue}, ${theme.pink})`,
                                outline: 'none',
                                cursor: 'pointer'
                              }}
                            />
                          </div>

                          {shapeTools.some(t => t.tool === currentTool) && (
                            <div style={{
                              padding: '15px',
                              background: `${theme.blue}10`,
                              borderRadius: '12px',
                              border: `2px solid ${theme.blue}30`
                            }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                <input 
                                  type="checkbox" 
                                  checked={fillShapes}
                                  onChange={(e) => setFillShapes(e.target.checked)}
                                  style={{ width: '16px', height: '16px' }}
                                />
                                <span style={{ fontSize: '12px', fontWeight: '600' }}>Fill shapes</span>
                              </label>
                              
                              <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '8px' }}>
                                  Pattern
                                </label>
                                <select 
                                  value={pattern}
                                  onChange={(e) => setPattern(e.target.value as any)}
                                  style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '2px solid #ddd',
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                    background: 'white'
                                  }}
                                >
                                  <option value="solid">Solid</option>
                                  <option value="dashed">Dashed</option>
                                  <option value="dotted">Dotted</option>
                                </select>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Color Palette */}
                        <div>
                          <h4 style={{
                            margin: '0 0 15px 0',
                            fontSize: '1rem',
                            fontWeight: '700',
                            color: theme.pink
                          }}>
                            🎨 Colors
                          </h4>
                          
                          <div style={{ display: 'flex', gap: '4px', marginBottom: '15px', flexWrap: 'wrap' }}>
                            {colorPalettes.map((palette, index) => (
                              <button
                                key={palette.name}
                                onClick={() => setActiveColorPalette(index)}
                                style={{
                                  padding: '6px 10px',
                                  fontSize: '10px',
                                  borderRadius: '8px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontWeight: '700',
                                  background: activeColorPalette === index 
                                    ? theme.primary 
                                    : '#f0f0f0',
                                  color: activeColorPalette === index ? 'white' : '#666'
                                }}
                              >
                                {palette.name}
                              </button>
                            ))}
                          </div>
                          
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '15px' }}>
                            {colorPalettes[activeColorPalette].colors.map(color => (
                              <button
                                key={color}
                                onClick={() => setCurrentColor(color)}
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '8px',
                                  border: `3px solid ${currentColor === color ? '#333' : '#ddd'}`,
                                  background: color,
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  transform: currentColor === color ? 'scale(1.1)' : 'scale(1)'
                                }}
                                title={color}
                              />
                            ))}
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input
                              type="color"
                              value={customColor}
                              onChange={(e) => {
                                setCustomColor(e.target.value);
                                setCurrentColor(e.target.value);
                              }}
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '8px',
                                border: '2px solid #ddd',
                                cursor: 'pointer'
                              }}
                            />
                            <span style={{ fontSize: '12px', fontWeight: '600', color: '#666' }}>Custom</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Background Panel */}
                    {activeToolTab === 'background' && (
                      <div>
                        <h3 style={{
                          margin: '0 0 20px 0',
                          fontSize: '1.2rem',
                          fontWeight: '800',
                          background: `linear-gradient(45deg, ${theme.success}, ${theme.blue})`,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}>
                          🌈 Background Colors
                        </h3>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '25px' }}>
                          {['#ffffff', '#f8f9fa', '#e9ecef', '#dee2e6', '#ffe5e5', '#e5f3ff', '#f0e5ff', '#e5ffe5', '#fff5e5', '#ffe5f5', '#f5f5f5', '#e5e5e5'].map(color => (
                            <button
                              key={color}
                              onClick={() => changeBackgroundColor(color)}
                              style={{
                                width: '50px',
                                height: '40px',
                                borderRadius: '8px',
                                border: `3px solid ${book[currentPageIndex]?.backgroundColor === color ? '#333' : '#ddd'}`,
                                background: color,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                transform: book[currentPageIndex]?.backgroundColor === color ? 'scale(1.1)' : 'scale(1)'
                              }}
                            />
                          ))}
                        </div>

                        <h4 style={{
                          margin: '0 0 15px 0',
                          fontSize: '1rem',
                          fontWeight: '700',
                          color: theme.accent
                        }}>
                          📐 Patterns
                        </h4>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '25px' }}>
                          {backgroundPatterns.map(pattern => (
                            <button
                              key={pattern.name}
                              onClick={() => changeBackgroundPattern(pattern.value)}
                              style={{
                                padding: '16px',
                                height: '60px',
                                borderRadius: '12px',
                                border: `3px solid ${book[currentPageIndex]?.backgroundPattern === pattern.value ? theme.accent : '#ddd'}`,
                                background: book[currentPageIndex]?.backgroundPattern === pattern.value 
                                  ? `${theme.accent}10` 
                                  : 'white',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '700',
                                backgroundImage: pattern.value !== 'none' ? pattern.value : undefined,
                                backgroundSize: '10px 10px',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              {pattern.name}
                            </button>
                          ))}
                        </div>

                        <h4 style={{
                          margin: '0 0 15px 0',
                          fontSize: '1rem',
                          fontWeight: '700',
                          color: theme.pink
                        }}>
                          📷 Background Image
                        </h4>
                        
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          style={{
                            width: '100%',
                            padding: '20px',
                            border: '3px dashed #ddd',
                            borderRadius: '12px',
                            background: 'white',
                            cursor: 'pointer',
                            textAlign: 'center',
                            marginBottom: '15px'
                          }}
                        >
                          <div style={{ fontSize: '24px', marginBottom: '8px' }}>📷</div>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#666' }}>Upload Image</div>
                        </button>
                        
                        {book[currentPageIndex]?.backgroundImage && (
                          <button
                            onClick={() => {
                              const updatedBook = [...book];
                              updatedBook[currentPageIndex] = {
                                ...updatedBook[currentPageIndex],
                                backgroundImage: undefined
                              };
                              setBook(updatedBook);
                            }}
                            style={{
                              width: '100%',
                              padding: '12px',
                              background: '#ffebee',
                              color: '#d32f2f',
                              border: '2px solid #ffcdd2',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}
                          >
                            Remove Background Image
                          </button>
                        )}
                        
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const imageUrl = URL.createObjectURL(file);
                              const updatedBook = [...book];
                              updatedBook[currentPageIndex] = {
                                ...updatedBook[currentPageIndex],
                                backgroundImage: imageUrl
                              };
                              setBook(updatedBook);
                            }
                          }}
                          accept="image/*"
                          style={{ display: 'none' }}
                        />
                      </div>
                    )}

                    {/* Writing Tips Panel */}
                    {activeToolTab === 'tips' && (
                      <div>
                        <h3 style={{
                          margin: '0 0 20px 0',
                          fontSize: '1.2rem',
                          fontWeight: '800',
                          background: `linear-gradient(45deg, ${theme.accent}, ${theme.pink})`,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}>
                          💡 Writing Tips
                        </h3>
                        
                        <div style={{ marginBottom: '25px' }}>
                          {[
                            "Start with 'Once upon a time...'",
                            "Create relatable characters",
                            "Every story needs a problem",
                            "Use descriptive words",
                            "Make dialogue natural",
                            "Keep sentences simple",
                            "End with satisfaction",
                            "Match pictures to words"
                          ].map((tip, index) => (
                            <div key={index} style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '12px',
                              padding: '12px',
                              background: `${theme.blue}10`,
                              borderRadius: '12px',
                              marginBottom: '8px',
                              border: `2px solid ${theme.blue}20`
                            }}>
                              <span style={{ color: theme.blue, fontWeight: '800', fontSize: '12px' }}>{index + 1}</span>
                              <span style={{ fontSize: '12px', color: '#333', lineHeight: '1.4' }}>{tip}</span>
                            </div>
                          ))}
                        </div>
                        
                        <div style={{
                          padding: '20px',
                          background: `linear-gradient(135deg, ${theme.secondary}10, ${theme.accent}10)`,
                          borderRadius: '15px',
                          border: `3px solid ${theme.secondary}30`
                        }}>
                          <h4 style={{
                            margin: '0 0 12px 0',
                            fontSize: '14px',
                            fontWeight: '700',
                            color: theme.secondary
                          }}>
                            🎯 Story Starter
                          </h4>
                          <p style={{
                            margin: '0 0 12px 0',
                            fontSize: '12px',
                            color: '#666',
                            fontWeight: '600'
                          }}>
                            Write a story about:
                          </p>
                          <p style={{
                            margin: '0 0 15px 0',
                            fontSize: '14px',
                            fontWeight: '700',
                            color: theme.secondary
                          }}>
                            {currentStoryStarter}
                          </p>
                          <button
                            onClick={getNewStoryStarter}
                            style={{
                              width: '100%',
                              padding: '10px',
                              background: `linear-gradient(135deg, ${theme.secondary}, ${theme.accent})`,
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '700'
                            }}
                          >
                            🎲 Get New Idea
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {leftPanelCollapsed && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '24px',
                    marginBottom: '10px',
                    color: theme.primary
                  }}>
                    🛠️
                  </div>
                  <div style={{
                    writingMode: 'vertical-rl',
                    fontSize: '12px',
                    fontWeight: '700',
                    color: theme.primary
                  }}>
                    Tools
                  </div>
                </div>
              )}
            </div>

            {/* Main Canvas Area */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {/* Canvas Controls */}
              <div style={{
                background: 'white',
                borderRadius: '15px 15px 0 0',
                padding: '20px',
                border: `3px solid ${theme.secondary}`,
                borderBottom: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button
                    onClick={() => setCurrentMode('select')}
                    style={{
                      padding: '10px 16px',
                      borderRadius: '10px',
                      border: `3px solid ${currentMode === 'select' ? theme.blue : '#ddd'}`,
                      background: currentMode === 'select' ? `${theme.blue}10` : 'white',
                      color: currentMode === 'select' ? theme.blue : '#666',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '700',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    🔍 Select
                  </button>
                  
                  <button
                    onClick={() => {
                      if (window.confirm('Clear everything on this page?')) {
                        saveState();
                        const updatedBook = [...book];
                        updatedBook[currentPageIndex] = {
                          ...updatedBook[currentPageIndex],
                          textElements: [],
                          drawingElements: []
                        };
                        setBook(updatedBook);
                        setSelectedElementId(null);
                        setSelectedDrawingId(null);
                      }
                    }}
                    style={{
                      padding: '10px 16px',
                      borderRadius: '10px',
                      border: '3px solid #ffcdd2',
                      background: '#ffebee',
                      color: '#d32f2f',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '700'
                    }}
                  >
                    🗑️ Clear
                  </button>
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: `${theme.accent}10`,
                  padding: '8px 16px',
                  borderRadius: '12px',
                  border: `2px solid ${theme.accent}30`
                }}>
                  <button 
                    onClick={prevPage} 
                    disabled={currentPageIndex === 0}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '2px solid #ddd',
                      background: 'white',
                      cursor: currentPageIndex === 0 ? 'not-allowed' : 'pointer',
                      opacity: currentPageIndex === 0 ? 0.5 : 1,
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    ← Prev
                  </button>
                  
                  <span style={{
                    padding: '8px 12px',
                    background: theme.accent,
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '800'
                  }}>
                    {currentPageIndex + 1} / {book.length}
                  </span>
                  
                  <button 
                    onClick={nextPage} 
                    disabled={currentPageIndex === book.length - 1}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '2px solid #ddd',
                      background: 'white',
                      cursor: currentPageIndex === book.length - 1 ? 'not-allowed' : 'pointer',
                      opacity: currentPageIndex === book.length - 1 ? 0.5 : 1,
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Next →
                  </button>
                </div>
              </div>
              
              {/* Canvas */}
              <div 
                ref={canvasRef}
                style={{
                  flex: 1,
                  border: `3px solid ${theme.secondary}`,
                  borderTop: 'none',
                  borderRadius: '0 0 15px 15px',
                  position: 'relative',
                  overflow: 'hidden',
                  background: 'white',
                  cursor: currentMode === 'draw' ? 'crosshair' : currentMode === 'text' ? 'text' : 'default',
                  transform: isFlipping ? 'scale(0.98)' : 'scale(1)',
                  transition: 'transform 0.3s ease',
                  ...getPageStyle(book[currentPageIndex])
                }}
                onMouseDown={handleCanvasMouseDown}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseUp}
              >
                {renderDrawings(book[currentPageIndex]?.drawingElements || [])}
                {renderTextElements(book[currentPageIndex]?.textElements || [])}
                
                {/* Mode indicator */}
                <div style={{
                  position: 'absolute',
                  top: '15px',
                  left: '15px',
                  padding: '8px 12px',
                  background: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {currentMode === 'draw' && `🎨 ${currentTool} • ${brushSize}px`}
                  {currentMode === 'text' && '📝 Click to add text'}
                  {currentMode === 'select' && '🔍 Click to select'}
                </div>
              </div>
            </div>

            {/* Right Panel - Pages */}
            <div style={{
              width: rightPanelCollapsed ? '60px' : '280px',
              transition: 'width 0.3s ease',
              background: 'white',
              borderRadius: '20px',
              boxShadow: '0 12px 35px rgba(0,0,0,0.1)',
              border: `3px solid ${theme.success}`,
              position: 'relative',
              overflow: 'hidden'
            }}>
              <button
                onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
                style={{
                  position: 'absolute',
                  top: '20px',
                  left: '15px',
                  background: theme.success,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  zIndex: 10
                }}
              >
                {rightPanelCollapsed ? '←' : '→'}
              </button>

              {!rightPanelCollapsed && (
                <div style={{ padding: '25px', height: '100%', overflowY: 'auto' }}>
                  <h3 style={{
                    margin: '0 0 20px 0',
                    fontSize: '1.2rem',
                    fontWeight: '800',
                    background: `linear-gradient(45deg, ${theme.success}, ${theme.blue})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    📖 Pages
                  </h3>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                    {book.map((page, index) => (
                      <div
                        key={page.id}
                        onClick={() => navigateToPage(index)}
                        style={{
                          position: 'relative',
                          padding: '12px',
                          borderRadius: '12px',
                          border: `3px solid ${currentPageIndex === index ? theme.success : '#ddd'}`,
                          background: currentPageIndex === index 
                            ? `${theme.success}10` 
                            : 'white',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (currentPageIndex !== index) {
                            e.currentTarget.style.borderColor = theme.success;
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentPageIndex !== index) {
                            e.currentTarget.style.borderColor = '#ddd';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        <div 
                          style={{
                            width: '100%',
                            height: '80px',
                            borderRadius: '8px',
                            border: '1px solid #ddd',
                            background: 'white',
                            position: 'relative',
                            overflow: 'hidden',
                            marginBottom: '8px',
                            ...getPageStyle(page)
                          }}
                        >
                          <div style={{ position: 'absolute', inset: 0, opacity: 0.6, transform: 'scale(0.8)' }}>
                            {renderDrawings(page.drawingElements)}
                            {renderTextElements(page.textElements)}
                          </div>
                        </div>
                        
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div style={{
                            fontSize: '12px',
                            fontWeight: '700',
                            color: '#333'
                          }}>
                            Page {index + 1}
                          </div>
                          
                          {book.length > 1 && (
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm('Delete this page?')) {
                                  const updatedBook = [...book];
                                  updatedBook.splice(index, 1);
                                  setBook(updatedBook);
                                  if (currentPageIndex >= updatedBook.length) {
                                    setCurrentPageIndex(updatedBook.length - 1);
                                  }
                                }
                              }}
                              style={{
                                background: '#ffebee',
                                color: '#d32f2f',
                                border: '2px solid #ffcdd2',
                                borderRadius: '6px',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: '10px'
                              }}
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    <button 
                      onClick={addNewPage}
                      style={{
                        width: '100%',
                        padding: '20px',
                        border: '3px dashed #ddd',
                        borderRadius: '12px',
                        background: 'white',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = theme.success;
                        e.currentTarget.style.background = `${theme.success}05`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#ddd';
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      <div style={{ fontSize: '24px', color: '#ccc', marginBottom: '8px' }}>+</div>
                      <div style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Add New Page</div>
                    </button>
                  </div>
                </div>
              )}

              {rightPanelCollapsed && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '24px',
                    marginBottom: '10px',
                    color: theme.success
                  }}>
                    📖
                  </div>
                  <div style={{
                    writingMode: 'vertical-rl',
                    fontSize: '12px',
                    fontWeight: '700',
                    color: theme.success
                  }}>
                    Pages
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PictureBookEditor;